import React, { useState } from 'react';
import { map, get } from 'lodash';
import { Alert, Button, Slider, ButtonToolbar } from 'angieslist-react-components';
import { Track } from 'angieslist-app-platform';
import { Field, Form, reduxForm } from 'redux-form';
import { InputField } from 'angieslist-app-platform/lib/browser/forms';
import ImageEditor from './ImageEditor';

type Props = {
  imagesToEdit: Object,
  updateShowcaseMedia: () => void,
  handleSubmit: () => void,
  updateShowcaseMediaError: Object,
  handleClose: () => void,
  updateShowcaseMediaReset: () => void,
  saveMediaReset: () => void,
  updateShowcaseMediaIsLoading: Boolean,
  updateShowcaseMediaResponse: Object,
  rotationEnabled: boolean,
};

const EditImageForm = ({
  imagesToEdit,
  updateShowcaseMedia,
  handleSubmit,
  handleClose,
  updateShowcaseMediaError,
  updateShowcaseMediaReset,
  updateShowcaseMediaIsLoading,
  updateShowcaseMediaResponse,
  saveMediaReset,
  rotationEnabled,
}: Props) => {
  const [rotate, setRotate] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageCanvasLoading, setImageCanvasLoading] = useState(true);
  const [editors, setEditors] = useState([]);

  const isFirstImage = currentImageIndex === 0;
  const isLastImage = currentImageIndex + 1 === imagesToEdit.length;
  const saveNextButtonText = isLastImage ? 'Save' : 'Save & Next Image';

  // eslint-disable-next-line no-return-assign
  const setSliderRef = slider => this.slider = slider;

  const setImageEditorRef = (imageEditorRef) => {
    if (imageEditorRef && !editors.includes(imageEditorRef)) {
      setEditors(prevState => prevState.concat([imageEditorRef]));
    }
  };

  const onSliderChange = index => setCurrentImageIndex(index);

  const handleImageUpdate = (values: any) => {
    const canvas = editors
      .find(editor => editor.props.image === imagesToEdit[currentImageIndex].url)
      .getImage();
    let imageType = imagesToEdit[currentImageIndex].path.split('.').pop();
    if (imageType === 'jpg') { imageType = 'jpeg'; }
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          updateShowcaseMedia(isLastImage, values, blob)
            .then(() => {
              if (isLastImage) {
                updateShowcaseMediaReset();
                saveMediaReset();
              }
              if (this.slider && get(updateShowcaseMediaResponse, 'path', false)) {
                this.slider.slickNext();
              }
              resolve();
            })
            .catch(reject);
        },
        `image/${imageType}`,
      );
    });
  };

  const sliderPrev = () => {
    if (this.slider) {
      this.slider.slickPrev();
    }
  };

  return (
    <div>
      <div>
        <p className="text-center">
          <strong>Image {currentImageIndex + 1} of {imagesToEdit.length}</strong>
        </p>
      </div>
      <Form
        onSubmit={
          handleSubmit(values =>
           handleImageUpdate({
             ...values,
             mediaId: imagesToEdit[currentImageIndex].mediaId
              || imagesToEdit[currentImageIndex].id,
           }))
        }
        name="EditMultipleImageForm"
      >
        <div className="slick-container" style={{ padding: '30px', background: 'transparent' }}>
          <Slider
            arrows={false}
            afterChange={onSliderChange}
            infinite={false}
            draggable={false}
            touchMove={false}
            ref={setSliderRef}
          >
            {map(imagesToEdit, (mediaEntry: Object, index: any) => {
              if (!mediaEntry.mediaId) {
                mediaEntry.mediaId = mediaEntry.id; // eslint-disable-line no-param-reassign
              }
              return (
                <div key={`div-${mediaEntry.mediaId}`}>
                  <ImageEditor
                    mediaId={mediaEntry.mediaId}
                    url={mediaEntry.url}
                    rotate={rotate}
                    setRotate={setRotate}
                    setImageEditorRef={setImageEditorRef}
                    imageCanvasLoading={imageCanvasLoading}
                    setImageCanvasLoading={setImageCanvasLoading}
                    rotationEnabled={rotationEnabled}
                  />
                  <Field
                    name={`imageDescriptionField_${mediaEntry.mediaId}`}
                    label="Image Description"
                    id={`Edit-Image-Modal--Image-Description-${mediaEntry.mediaId}`}
                    component={InputField}
                    type="textarea"
                    style={{ height: '5em' }}
                    placeholder="Please enter a description here"
                  />
                </div>
              );
              })
            }
          </Slider>
          <If condition={updateShowcaseMediaError}>
            <Alert bsStyle="danger" className="text-center">
              There was an error editing your image, please try again.
            </Alert>
          </If>
          <ButtonToolbar className="text-right">
            <Track>
              <Button
                id="Edit-Multiple-Images-Modal--Cancel"
                bsSize="small"
                bsStyle="default-office"
                onClick={handleClose}
              >
                Cancel
              </Button>
            </Track>
            <If condition={!isFirstImage}>
              <Track>
                <Button
                  id="Edit-Multiple-Images-Modal--Back"
                  bsSize="small"
                  flat
                  onClick={sliderPrev}
                >
                  Back
                </Button>
              </Track>
            </If>
            <Track>
              <Button
                id="Edit-Multiple-Images-Modal--SaveNext"
                loading={imageCanvasLoading}
                disable={imageCanvasLoading}
                bsSize="small"
                bsStyle="primary-office"
                flat
                type="submit"
                className={updateShowcaseMediaIsLoading ? 'btn-loading' : null}
              >
                {saveNextButtonText}
              </Button>
            </Track>
          </ButtonToolbar>
        </div>
      </Form>
    </div>
  );
};

export default (
  reduxForm({
    form: 'EditMultipleImageForm',
    onSubmitSuccess: (result, sliderNext, props) => {
      if (!props.setSubmitSucceeded().error) {
        this.slider.slickNext();
      }
    },
  }))(EditImageForm);



// WEBPACK FOOTER //
// ./src/modules/showcase/browser/components/EditImageForm.js